import { useEffect, useState } from 'react';
import { listMembers } from '../../api/clubs';
import type { ClubMemberResponse } from '../../types/api';
import { initials } from '../../lib/avatar';

interface Props {
  clubId: number;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

export function ParticipantPicker({ clubId, selectedIds, onChange }: Props) {
  const [members, setMembers] = useState<ClubMemberResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    listMembers(clubId)
      .then(setMembers)
      .catch(() => setError('Failed to load members.'))
      .finally(() => setLoading(false));
  }, [clubId]);

  function toggleMember(userId: string) {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter(id => id !== userId));
    } else {
      onChange([...selectedIds, userId]);
    }
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map(i => (
          <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
        ))}
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-500">{error}</p>;
  }

  if (members.length === 0) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">No club members found.</p>;
  }

  return (
    <div className="space-y-2">
      {members.map(member => {
        const selected = selectedIds.includes(member.userId);
        const label = initials(member.abbreviation, member.displayName);
        return (
          <label
            key={member.userId}
            className="flex items-center gap-3 cursor-pointer rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
          >
            <input
              type="checkbox"
              checked={selected}
              onChange={() => toggleMember(member.userId)}
              className="rounded border-slate-300 dark:border-slate-600 text-teal-600 focus:ring-teal-500"
            />
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100 dark:bg-teal-900/40 text-xs font-bold text-teal-700 dark:text-teal-400">
              {label}
            </span>
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{member.displayName}</span>
            {(member.role === 'Owner' || member.role === 'Admin') && (
              <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">{member.role}</span>
            )}
          </label>
        );
      })}
    </div>
  );
}
